import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

// ── HELPERS ───────────────────────────────────────────────────────────────────
function getDayOfYear(d = new Date()) {
  return Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
}
function getWeekNumber(d = new Date()) {
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
}
function seededShuffle(arr, seed) {
  const a = [...arr]; let s = seed | 0;
  for (let i = a.length - 1; i > 0; i--) {
    s = (Math.imul(s ^ (s >>> 16), 0x45d9f3b) ^ Math.imul(s, 0x119de1f3));
    s = (s ^ (s >>> 16)) | 0;
    const j = Math.abs(s) % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function todayISO() { return new Date().toISOString().split('T')[0]; }
function getTodayGameType() { return GAME_TYPES[getDayOfYear() % GAME_TYPES.length]; }
function getTodayPhrase() { return PHRASES[getDayOfYear() % PHRASES.length]; }
function getTodayQuestions(type, n = 6) {
  const pool = { flashcard: FLASH_CARDS, complete: COMPLETE_SENTENCES, wordbuilder: WORD_BUILDER, oddout: ODD_ONE_OUT, phrasal: PHRASAL_VERBS }[type] || [];
  return seededShuffle(pool, getDayOfYear() * 31 + GAME_TYPES.indexOf(type)).slice(0, n);
}
function getCurrentMission() { return MISSIONS[getWeekNumber() % MISSIONS.length]; }

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const GAME_TYPES = ['flashcard', 'complete', 'wordbuilder', 'oddout', 'phrasal'];
const GAME_INFO = {
  flashcard:   { icon: '🃏', name: 'Flash Cards',       desc: 'Verbos irregulares EN ↔ PT',  color: '#7c3aed', bg: 'linear-gradient(135deg,#7c3aed,#6d28d9)' },
  complete:    { icon: '✏️', name: 'Complete a Frase',   desc: 'Escolha a opção correta',     color: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#0369a1)' },
  wordbuilder: { icon: '🔤', name: 'Monte a Frase',      desc: 'Toque nas palavras em ordem', color: '#16a34a', bg: 'linear-gradient(135deg,#16a34a,#15803d)' },
  oddout:      { icon: '🔍', name: 'O Intruso',          desc: 'Qual não pertence ao grupo?', color: '#d97706', bg: 'linear-gradient(135deg,#d97706,#b45309)' },
  phrasal:     { icon: '🔗', name: 'Phrasal Verbs',      desc: 'Escolha o significado certo', color: '#be185d', bg: 'linear-gradient(135deg,#be185d,#9d174d)' },
};

// ── PHRASES (60) ──────────────────────────────────────────────────────────────
const PHRASES = [
  { en: 'Touch base', pt: 'Entrar em contato / fazer um check-in rápido', ex: '"Let\'s touch base tomorrow to align on the project."' },
  { en: 'Get the ball rolling', pt: 'Dar início a algo / começar o processo', ex: '"Who\'s going to get the ball rolling on this initiative?"' },
  { en: 'Think outside the box', pt: 'Pensar de forma criativa e inovadora', ex: '"We need to think outside the box to solve this problem."' },
  { en: 'Circle back', pt: 'Retornar ao assunto / dar um retorno depois', ex: '"Let me circle back to you on that by end of day."' },
  { en: 'Move the needle', pt: 'Gerar impacto real / fazer a diferença', ex: '"Which actions will actually move the needle on our sales?"' },
  { en: 'Low-hanging fruit', pt: 'O mais fácil de conquistar / vitórias rápidas', ex: '"Let\'s start with the low-hanging fruit before tackling bigger issues."' },
  { en: 'Keep someone in the loop', pt: 'Manter alguém informado / atualizado', ex: '"Keep me in the loop on any updates from the client."' },
  { en: 'Hit the ground running', pt: 'Começar com tudo / sem perder tempo', ex: '"The new hire hit the ground running from day one."' },
  { en: 'On the same page', pt: 'Em sintonia / com o mesmo entendimento', ex: '"Before we proceed, let\'s make sure we\'re all on the same page."' },
  { en: 'Pick someone\'s brain', pt: 'Pedir conselhos / aproveitar o conhecimento de alguém', ex: '"Could I pick your brain about the new strategy?"' },
  { en: 'At the end of the day', pt: 'No fim das contas / em última análise', ex: '"At the end of the day, customer satisfaction is what matters most."' },
  { en: 'Going forward', pt: 'Daqui em diante / a partir de agora', ex: '"Going forward, all reports must be submitted on Mondays."' },
  { en: 'Ballpark figure', pt: 'Estimativa aproximada / valor de referência', ex: '"Can you give me a ballpark figure for the project cost?"' },
  { en: 'Red tape', pt: 'Burocracia excessiva / processos lentos', ex: '"There\'s too much red tape to get anything approved quickly."' },
  { en: 'Bite the bullet', pt: 'Encarar algo difícil / aceitar o inevitável', ex: '"We need to bite the bullet and cut the marketing budget."' },
  { en: 'Back to square one', pt: 'Começar do zero / recomeçar tudo', ex: '"The client rejected the proposal, so we\'re back to square one."' },
  { en: 'Cutting-edge', pt: 'De ponta / a tecnologia ou técnica mais avançada', ex: '"We use cutting-edge software to manage our operations."' },
  { en: 'In a nutshell', pt: 'Em resumo / de forma simplificada e direta', ex: '"In a nutshell, our Q3 results exceeded all expectations."' },
  { en: 'No-brainer', pt: 'Decisão óbvia / algo que não precisa de muito raciocínio', ex: '"Choosing the faster supplier was a no-brainer."' },
  { en: 'Walk the talk', pt: 'Praticar o que prega / coerência entre discurso e ação', ex: '"Great leaders walk the talk — they set the example."' },
  { en: 'Break the ice', pt: 'Quebrar o gelo / iniciar uma conversa', ex: '"He started with a joke to break the ice at the conference."' },
  { en: 'Drop the ball', pt: 'Deixar algo escapar / falhar em uma responsabilidade', ex: '"We dropped the ball on the client\'s request — I apologize."' },
  { en: 'Get the hang of it', pt: 'Pegar o jeito / dominar algo com prática', ex: '"The software looks complex, but you\'ll get the hang of it."' },
  { en: 'Have a lot on your plate', pt: 'Ter muita coisa para fazer / estar sobrecarregado', ex: '"I can\'t take on new tasks — I have a lot on my plate."' },
  { en: 'In the long run', pt: 'A longo prazo / com o tempo', ex: '"Investing in training pays off in the long run."' },
  { en: 'Miss the boat', pt: 'Perder a oportunidade / chegar tarde demais', ex: '"If we don\'t act now, we\'ll miss the boat on this market."' },
  { en: 'On the fence', pt: 'Indeciso / sem tomar partido', ex: '"The board is still on the fence about the new acquisition."' },
  { en: 'Read between the lines', pt: 'Entender o que não foi dito / interpretar as entrelinhas', ex: '"Read between the lines — the client is not happy with the results."' },
  { en: 'See eye to eye', pt: 'Concordar / ter a mesma opinião', ex: '"We don\'t see eye to eye on the pricing strategy."' },
  { en: 'Up in the air', pt: 'Incerto / indefinido / sem decisão tomada', ex: '"The merger is still up in the air — no decision has been made."' },
  { en: 'Beat around the bush', pt: 'Enrolar / não ir direto ao ponto', ex: '"Stop beating around the bush — what is the real issue?"' },
  { en: 'Catch someone off guard', pt: 'Pegar alguém de surpresa / despreparado', ex: '"The competitor\'s announcement caught us off guard."' },
  { en: 'Under the radar', pt: 'Passar despercebido / sem chamar atenção', ex: '"This trend has been flying under the radar, but it\'s growing fast."' },
  { en: 'Silver lining', pt: 'O lado positivo de uma situação ruim', ex: '"The silver lining of the crisis was that it forced us to innovate."' },
  { en: 'Face the music', pt: 'Encarar as consequências / assumir a responsabilidade', ex: '"It\'s time to face the music and admit we missed the target."' },
  { en: 'Get a head start', pt: 'Sair na frente / começar antes dos outros', ex: '"Starting the project in January gives us a head start."' },
  { en: 'Jump to conclusions', pt: 'Tirar conclusões precipitadas / sem analisar bem', ex: '"Don\'t jump to conclusions — wait for all the data."' },
  { en: 'Long shot', pt: 'Uma chance pequena / algo difícil mas possível', ex: '"Closing this deal in one week is a long shot, but we\'ll try."' },
  { en: 'Pay it forward', pt: 'Retribuir o bem recebido ajudando outra pessoa', ex: '"She helped me when I started — I want to pay it forward."' },
  { en: 'Throw in the towel', pt: 'Desistir / jogar a toalha / abandonar', ex: '"After three failed pitches, the team decided to throw in the towel."' },
  { en: 'Boil the ocean', pt: 'Tentar fazer o impossível / complicar demais', ex: '"We can\'t boil the ocean — focus on one market segment first."' },
  { en: 'Push the envelope', pt: 'Ir além dos limites / inovar / arriscar mais', ex: '"This campaign really pushes the envelope in terms of creativity."' },
  { en: 'Draw the line', pt: 'Estabelecer um limite / não aceitar mais', ex: '"We need to draw the line on scope creep or we\'ll never finish."' },
  { en: 'Bring to the table', pt: 'Contribuir com / oferecer algo de valor', ex: '"What does this new partner bring to the table?"' },
  { en: 'Ahead of the curve', pt: 'À frente do mercado / mais avançado que a concorrência', ex: '"Our R&D team keeps us ahead of the curve."' },
  { en: 'Get on board', pt: 'Embarcar / aceitar / apoiar uma ideia', ex: '"We need the entire team to get on board with this change."' },
  { en: 'Raise the bar', pt: 'Elevar o padrão / aumentar as expectativas', ex: '"This product launch raised the bar for the entire industry."' },
  { en: 'Cut to the chase', pt: 'Ir direto ao ponto / sem rodeios', ex: '"Let\'s cut to the chase — are you interested or not?"' },
  { en: 'Back to basics', pt: 'Voltar ao essencial / ao fundamental', ex: '"After over-engineering it, we went back to basics."' },
  { en: 'Burn bridges', pt: 'Arruinar relacionamentos / queimar pontes', ex: '"Don\'t burn bridges — you never know when you\'ll need a reference."' },
  { en: 'On thin ice', pt: 'Em terreno perigoso / arriscando uma situação delicada', ex: '"He\'s on thin ice after missing two deadlines in a row."' },
  { en: 'Steep learning curve', pt: 'Muito desafiador de aprender / curva de aprendizado íngreme', ex: '"This platform has a steep learning curve, but it\'s worth it."' },
  { en: 'Bottom line', pt: 'O ponto principal / o resultado final / o lucro líquido', ex: '"The bottom line is: this strategy isn\'t working."' },
  { en: 'Take the lead', pt: 'Assumir a liderança / tomar a frente', ex: '"I\'d like you to take the lead on the client presentation."' },
  { en: 'Set the record straight', pt: 'Corrigir um mal-entendido / esclarecer algo', ex: '"I want to set the record straight about what happened in Q2."' },
  { en: 'Take something on board', pt: 'Considerar / levar em conta / aceitar um feedback', ex: '"Thank you for the feedback — I\'ll take it on board."' },
  { en: 'Drill down', pt: 'Aprofundar / entrar em detalhes', ex: '"Let\'s drill down into the data before making a decision."' },
  { en: 'Bandwidth', pt: 'Capacidade / disponibilidade de tempo ou recursos', ex: '"I don\'t have the bandwidth to take on a new project right now."' },
  { en: 'Leverage', pt: 'Usar como vantagem estratégica / alavancar', ex: '"We can leverage our existing network to enter this market faster."' },
  { en: 'Pivot', pt: 'Mudar de direção / estratégia de forma rápida', ex: '"The startup had to pivot when the original model didn\'t work."' },
  { en: 'Synergy', pt: 'Sinergia / resultado superior gerado pela colaboração', ex: '"The merger created synergy between our two sales teams."' },
];

// ── FLASH CARDS ───────────────────────────────────────────────────────────────
const FLASH_CARDS = [
  { front: 'run', back: 'ran • run', pt: 'correr' },
  { front: 'write', back: 'wrote • written', pt: 'escrever' },
  { front: 'speak', back: 'spoke • spoken', pt: 'falar' },
  { front: 'take', back: 'took • taken', pt: 'pegar / levar' },
  { front: 'give', back: 'gave • given', pt: 'dar' },
  { front: 'know', back: 'knew • known', pt: 'saber / conhecer' },
  { front: 'think', back: 'thought • thought', pt: 'pensar' },
  { front: 'bring', back: 'brought • brought', pt: 'trazer' },
  { front: 'buy', back: 'bought • bought', pt: 'comprar' },
  { front: 'catch', back: 'caught • caught', pt: 'pegar / capturar' },
  { front: 'choose', back: 'chose • chosen', pt: 'escolher' },
  { front: 'drive', back: 'drove • driven', pt: 'dirigir' },
  { front: 'fall', back: 'fell • fallen', pt: 'cair' },
  { front: 'feel', back: 'felt • felt', pt: 'sentir' },
  { front: 'find', back: 'found • found', pt: 'encontrar' },
  { front: 'forget', back: 'forgot • forgotten', pt: 'esquecer' },
  { front: 'grow', back: 'grew • grown', pt: 'crescer' },
  { front: 'hear', back: 'heard • heard', pt: 'ouvir' },
  { front: 'hold', back: 'held • held', pt: 'segurar / manter' },
  { front: 'keep', back: 'kept • kept', pt: 'guardar / manter' },
  { front: 'lead', back: 'led • led', pt: 'liderar' },
  { front: 'leave', back: 'left • left', pt: 'deixar / partir' },
  { front: 'lose', back: 'lost • lost', pt: 'perder' },
  { front: 'mean', back: 'meant • meant', pt: 'significar' },
  { front: 'meet', back: 'met • met', pt: 'conhecer / se reunir' },
  { front: 'pay', back: 'paid • paid', pt: 'pagar' },
  { front: 'sell', back: 'sold • sold', pt: 'vender' },
  { front: 'send', back: 'sent • sent', pt: 'enviar' },
  { front: 'spend', back: 'spent • spent', pt: 'gastar / passar (tempo)' },
  { front: 'teach', back: 'taught • taught', pt: 'ensinar' },
  { front: 'tell', back: 'told • told', pt: 'contar / dizer' },
  { front: 'understand', back: 'understood • understood', pt: 'entender' },
  { front: 'win', back: 'won • won', pt: 'vencer / ganhar' },
  { front: 'begin', back: 'began • begun', pt: 'começar' },
  { front: 'break', back: 'broke • broken', pt: 'quebrar' },
  { front: 'build', back: 'built • built', pt: 'construir' },
  { front: 'read', back: 'read • read', pt: 'ler' },
  { front: 'stand', back: 'stood • stood', pt: 'ficar de pé / suportar' },
  { front: 'show', back: 'showed • shown', pt: 'mostrar' },
  { front: 'cut', back: 'cut • cut', pt: 'cortar' },
];

// ── COMPLETE THE SENTENCE ─────────────────────────────────────────────────────
const COMPLETE_SENTENCES = [
  { sentence: 'Could you please _____ the report by Friday?', options: ['send', 'sent', 'sending', 'to send'], answer: 0, tip: 'Após "please", use o verbo no infinitivo base.' },
  { sentence: 'She has been _____ this company for 10 years.', options: ['run', 'running', 'ran', 'runs'], answer: 1, tip: '"Has been + -ing" = present perfect continuous.' },
  { sentence: 'I would rather _____ by email than by phone.', options: ['communicate', 'communicating', 'communicated', 'to communicate'], answer: 0, tip: 'Após "would rather", use o infinitivo base.' },
  { sentence: 'The meeting was _____ to next Tuesday.', options: ['postponed', 'postpone', 'postponing', 'to postpone'], answer: 0, tip: '"Was postponed" = voz passiva no passado simples.' },
  { sentence: 'We need to _____ this issue as soon as possible.', options: ['address', 'addressed', 'addressing', 'addresses'], answer: 0, tip: '"Need to" é seguido pelo infinitivo base.' },
  { sentence: 'If I _____ you, I would accept the offer.', options: ['were', 'am', 'was', 'be'], answer: 0, tip: 'No condicional, usamos "were" para todos os sujeitos (formal/subjuntivo).' },
  { sentence: 'I look forward to _____ from you soon.', options: ['hearing', 'hear', 'heard', 'have heard'], answer: 0, tip: '"Look forward to" é sempre seguido de gerúndio (-ing).' },
  { sentence: 'He suggested _____ the budget before the launch.', options: ['reviewing', 'review', 'to review', 'reviewed'], answer: 0, tip: '"Suggest" é seguido de gerúndio (-ing).' },
  { sentence: 'Despite _____ hard, she missed the deadline.', options: ['working', 'work', 'worked', 'to work'], answer: 0, tip: '"Despite" é seguido de gerúndio (-ing).' },
  { sentence: 'The project will be completed _____ the end of March.', options: ['by', 'until', 'for', 'since'], answer: 0, tip: '"By" indica prazo final; "until" indica duração contínua.' },
  { sentence: 'She is used to _____ under pressure.', options: ['working', 'work', 'worked', 'works'], answer: 0, tip: '"Used to" como adjetivo (= acostumado) pede gerúndio.' },
  { sentence: 'We should _____ the client before making any changes.', options: ['consult', 'consulted', 'consulting', 'to consult'], answer: 0, tip: 'Verbos modais ("should") são seguidos pelo infinitivo base.' },
  { sentence: 'The team has _____ significant progress this quarter.', options: ['made', 'make', 'making', 'makes'], answer: 0, tip: '"Has made" = present perfect. "Make progress" é uma colocação fixa.' },
  { sentence: 'It\'s essential that every team member _____ the briefing.', options: ['attend', 'attends', 'attended', 'attending'], answer: 0, tip: 'Após "it\'s essential that", usamos o subjuntivo (infinitivo base).' },
  { sentence: 'By the time you arrive, we _____ the presentation.', options: ['will have finished', 'will finish', 'finish', 'finished'], answer: 0, tip: 'Future perfect: ação concluída antes de outro momento futuro.' },
  { sentence: 'The product _____ in three markets last year.', options: ['was launched', 'launched', 'has launched', 'launch'], answer: 0, tip: 'Voz passiva no passado: "was + particípio".' },
  { sentence: 'I wish I _____ more experience when I started.', options: ['had had', 'have', 'had', 'would have'], answer: 0, tip: '"Wish" sobre o passado usa past perfect (had + particípio).' },
  { sentence: 'The figures don\'t _____ up — something is wrong.', options: ['add', 'adding', 'added', 'to add'], answer: 0, tip: '"Add up" = phrasal verb que significa "fazer sentido / bater as contas".' },
  { sentence: 'I\'d rather you _____ without telling me first.', options: ['didn\'t leave', 'don\'t leave', 'not leave', 'haven\'t left'], answer: 0, tip: '"I\'d rather + sujeito + passado simples" expressa preferência sobre outros.' },
  { sentence: 'We\'ll proceed _____ we receive the client\'s approval.', options: ['once', 'while', 'during', 'despite'], answer: 0, tip: '"Once" significa "assim que" neste contexto.' },
  { sentence: 'The report is _____ a thorough review of the market.', options: ['based on', 'based in', 'basing on', 'base on'], answer: 0, tip: '"Based on" = colocação preposicional fixa.' },
  { sentence: 'I am writing to _____ my interest in the position.', options: ['express', 'expressing', 'expressed', 'expression'], answer: 0, tip: '"Writing to" é seguido pelo infinitivo base.' },
  { sentence: 'The deadline has been _____ until next month.', options: ['extended', 'extend', 'extending', 'extends'], answer: 0, tip: '"Has been extended" = present perfect passivo.' },
  { sentence: 'Could we _____ our call for Thursday afternoon?', options: ['reschedule', 'rescheduling', 'rescheduled', 'to reschedule'], answer: 0, tip: 'Após "could we", use o infinitivo base.' },
  { sentence: 'Please find _____ the document you requested.', options: ['attached', 'attach', 'attaching', 'to attach'], answer: 0, tip: '"Find attached" é uma expressão formal fixa em e-mails profissionais.' },
];

// ── WORD BUILDER ──────────────────────────────────────────────────────────────
const WORD_BUILDER = [
  { words: ['I', 'would', 'like', 'to', 'schedule', 'a', 'meeting'], pt: 'Gostaria de agendar uma reunião.' },
  { words: ['Could', 'you', 'send', 'me', 'the', 'report', 'by', 'Friday', '?'], pt: 'Você poderia me enviar o relatório até sexta?' },
  { words: ['We', 'need', 'to', 'address', 'this', 'issue', 'immediately'], pt: 'Precisamos tratar este assunto imediatamente.' },
  { words: ['I', 'look', 'forward', 'to', 'hearing', 'from', 'you'], pt: 'Aguardo seu retorno.' },
  { words: ['Please', 'let', 'me', 'know', 'if', 'you', 'have', 'any', 'questions'], pt: 'Me avise se tiver alguma dúvida.' },
  { words: ['The', 'deadline', 'has', 'been', 'moved', 'to', 'next', 'Monday'], pt: 'O prazo foi alterado para a próxima segunda.' },
  { words: ['I', 'apologize', 'for', 'any', 'inconvenience', 'caused'], pt: 'Me desculpe por qualquer inconveniente causado.' },
  { words: ['I', 'will', 'get', 'back', 'to', 'you', 'as', 'soon', 'as', 'possible'], pt: 'Entrarei em contato assim que possível.' },
  { words: ['We', 'are', 'pleased', 'to', 'announce', 'our', 'new', 'partnership'], pt: 'Temos o prazer de anunciar nossa nova parceria.' },
  { words: ['Thank', 'you', 'for', 'your', 'time', 'and', 'consideration'], pt: 'Obrigado pelo seu tempo e consideração.' },
  { words: ['I', 'wanted', 'to', 'follow', 'up', 'on', 'our', 'last', 'conversation'], pt: 'Queria dar um retorno sobre nossa última conversa.' },
  { words: ['Please', 'find', 'attached', 'the', 'document', 'you', 'requested'], pt: 'Segue em anexo o documento que você solicitou.' },
  { words: ['I', 'am', 'writing', 'to', 'express', 'my', 'interest', 'in', 'this', 'role'], pt: 'Escrevo para expressar meu interesse nesta vaga.' },
  { words: ['Could', 'we', 'reschedule', 'our', 'call', 'for', 'tomorrow', '?'], pt: 'Poderíamos remarcar nossa ligação para amanhã?' },
  { words: ['We', 'look', 'forward', 'to', 'working', 'with', 'you'], pt: 'Aguardamos a oportunidade de trabalhar com vocês.' },
];

// ── ODD ONE OUT ───────────────────────────────────────────────────────────────
const ODD_ONE_OUT = [
  { words: ['run', 'jump', 'beautiful', 'walk'], answer: 'beautiful', reason: '"Beautiful" é adjetivo; os outros são verbos de ação.' },
  { words: ['angry', 'happy', 'quickly', 'sad'], answer: 'quickly', reason: '"Quickly" é advérbio; os outros são adjetivos de emoção.' },
  { words: ['meeting', 'conference', 'deadline', 'workshop'], answer: 'deadline', reason: '"Deadline" é um prazo; os outros são eventos e encontros.' },
  { words: ['email', 'report', 'proposal', 'listen'], answer: 'listen', reason: '"Listen" é verbo; os outros são tipos de documento.' },
  { words: ['CEO', 'manager', 'director', 'invoice'], answer: 'invoice', reason: '"Invoice" é documento financeiro; os outros são cargos.' },
  { words: ['negotiate', 'collaborate', 'delegate', 'budget'], answer: 'budget', reason: '"Budget" é substantivo; os outros são verbos profissionais.' },
  { words: ['client', 'customer', 'supplier', 'presentation'], answer: 'presentation', reason: '"Presentation" é uma ação; os outros são pessoas/parceiros.' },
  { words: ['improve', 'enhance', 'optimize', 'revenue'], answer: 'revenue', reason: '"Revenue" é substantivo; os outros são verbos de melhoria.' },
  { words: ['resign', 'quit', 'retire', 'promote'], answer: 'promote', reason: '"Promote" significa avançar; os outros indicam saída do cargo.' },
  { words: ['salary', 'bonus', 'raise', 'layoff'], answer: 'layoff', reason: '"Layoff" é demissão; os outros são formas de remuneração.' },
  { words: ['remote', 'hybrid', 'on-site', 'overtime'], answer: 'overtime', reason: '"Overtime" é horas extras; os outros são modelos de trabalho.' },
  { words: ['achieve', 'accomplish', 'succeed', 'fail'], answer: 'fail', reason: '"Fail" é negativo; os outros indicam sucesso ou realização.' },
  { words: ['brainstorm', 'ideate', 'workshop', 'approve'], answer: 'approve', reason: '"Approve" é validar; os outros são processos de geração de ideias.' },
  { words: ['formal', 'professional', 'polite', 'resign'], answer: 'resign', reason: '"Resign" é verbo; os outros são adjetivos de postura profissional.' },
  { words: ['agenda', 'minutes', 'schedule', 'strategy'], answer: 'strategy', reason: '"Strategy" é plano de longo prazo; os outros são documentos de reunião.' },
  { words: ['fluent', 'proficient', 'advanced', 'translate'], answer: 'translate', reason: '"Translate" é verbo; os outros são níveis de proficiência.' },
  { words: ['keynote', 'pitch', 'briefing', 'invoice'], answer: 'invoice', reason: '"Invoice" é financeiro; os outros são tipos de apresentação.' },
  { words: ['deadline', 'milestone', 'forecast', 'quarterly'], answer: 'quarterly', reason: '"Quarterly" é advérbio/adjetivo; os outros são substantivos de projeto.' },
  { words: ['feedback', 'review', 'appraisal', 'invoice'], answer: 'invoice', reason: '"Invoice" é financeiro; os outros são formas de avaliação de desempenho.' },
  { words: ['scale', 'grow', 'expand', 'downsize'], answer: 'downsize', reason: '"Downsize" é reduzir; os outros indicam crescimento ou expansão.' },
];

// ── PHRASAL VERBS ─────────────────────────────────────────────────────────────
const PHRASAL_VERBS = [
  { verb: 'follow up', pt: 'dar um retorno / acompanhar', options: ['dar um retorno / acompanhar', 'desistir de algo', 'adiar indefinidamente', 'revisar um plano'] },
  { verb: 'carry out', pt: 'realizar / executar', options: ['realizar / executar', 'cancelar um projeto', 'pedir uma opinião', 'negociar um prazo'] },
  { verb: 'put off', pt: 'adiar / postergar', options: ['adiar / postergar', 'confirmar uma reunião', 'liderar uma equipe', 'aceitar uma proposta'] },
  { verb: 'bring up', pt: 'mencionar / levantar um assunto', options: ['mencionar / levantar um assunto', 'encerrar uma discussão', 'aprovar uma ideia', 'delegar uma tarefa'] },
  { verb: 'set up', pt: 'organizar / configurar / fundar', options: ['organizar / configurar / fundar', 'encerrar uma empresa', 'cancelar um contrato', 'reprovar um projeto'] },
  { verb: 'take on', pt: 'assumir / aceitar um desafio', options: ['assumir / aceitar um desafio', 'rejeitar uma responsabilidade', 'delegar uma função', 'evitar um problema'] },
  { verb: 'break down', pt: 'detalhar em partes / colapsar', options: ['detalhar em partes / colapsar', 'consolidar dados', 'apresentar resultados', 'aprovar um budget'] },
  { verb: 'look into', pt: 'investigar / analisar com cuidado', options: ['investigar / analisar com cuidado', 'ignorar um problema', 'cancelar uma reunião', 'assinar um contrato'] },
  { verb: 'hand over', pt: 'transferir / passar a responsabilidade', options: ['transferir / passar a responsabilidade', 'reter informações', 'solicitar feedback', 'arquivar documentos'] },
  { verb: 'wrap up', pt: 'concluir / finalizar', options: ['concluir / finalizar', 'iniciar um projeto', 'expandir uma ideia', 'recrutar talentos'] },
  { verb: 'come up with', pt: 'criar / propor uma ideia ou solução', options: ['criar / propor uma ideia ou solução', 'rejeitar uma solução', 'apresentar resultados', 'reduzir custos'] },
  { verb: 'go over', pt: 'revisar / examinar em detalhes', options: ['revisar / examinar em detalhes', 'ignorar os detalhes', 'delegar a revisão', 'cancelar a análise'] },
  { verb: 'step down', pt: 'renunciar / deixar o cargo', options: ['renunciar / deixar o cargo', 'receber uma promoção', 'assumir mais responsabilidades', 'negociar um aumento'] },
  { verb: 'scale up', pt: 'crescer / aumentar a escala de operação', options: ['crescer / aumentar a escala de operação', 'reduzir o quadro', 'encerrar as atividades', 'cortar o orçamento'] },
  { verb: 'reach out', pt: 'entrar em contato / se aproximar', options: ['entrar em contato / se aproximar', 'ignorar um cliente', 'encerrar uma parceria', 'rejeitar uma proposta'] },
  { verb: 'turn down', pt: 'recusar / rejeitar uma oferta', options: ['recusar / rejeitar uma oferta', 'aceitar uma proposta', 'assinar um contrato', 'promover alguém'] },
  { verb: 'roll out', pt: 'lançar / implementar gradualmente', options: ['lançar / implementar gradualmente', 'encerrar um produto', 'reduzir o escopo', 'adiar indefinidamente'] },
  { verb: 'opt out', pt: 'recusar / não participar', options: ['recusar / não participar', 'se voluntariar para algo', 'assumir a liderança', 'aceitar os termos'] },
  { verb: 'sign off on', pt: 'aprovar formalmente', options: ['aprovar formalmente', 'cancelar um projeto', 'rejeitar uma proposta', 'pedir uma revisão'] },
  { verb: 'draw up', pt: 'redigir / elaborar documentos', options: ['redigir / elaborar documentos', 'destruir contratos', 'ignorar os detalhes', 'assinar sem ler'] },
  { verb: 'phase out', pt: 'eliminar gradualmente / descontinuar', options: ['eliminar gradualmente / descontinuar', 'expandir um produto', 'lançar nova versão', 'aumentar a produção'] },
  { verb: 'cut back', pt: 'reduzir / cortar gastos ou esforços', options: ['reduzir / cortar gastos ou esforços', 'expandir operações', 'contratar mais pessoas', 'lançar um produto'] },
  { verb: 'back up', pt: 'apoiar / confirmar com dados / fazer backup', options: ['apoiar / confirmar com dados / fazer backup', 'contestar uma decisão', 'cancelar uma reunião', 'delegar algo'] },
  { verb: 'fill in', pt: 'substituir alguém temporariamente', options: ['substituir alguém temporariamente', 'demitir um funcionário', 'assinar um contrato', 'revisar uma proposta'] },
  { verb: 'touch base', pt: 'entrar em contato para alinhar algo', options: ['entrar em contato para alinhar algo', 'encerrar uma parceria', 'revisar um contrato', 'cancelar um projeto'] },
];

// ── MISSIONS ──────────────────────────────────────────────────────────────────
const MISSIONS = [
  { icon: '📧', text: 'Envie um e-mail de trabalho completamente em inglês.', en: 'Send a work email entirely in English.' },
  { icon: '🗣️', text: 'Use uma frase em inglês em uma reunião ou conversa real.', en: 'Use an English phrase in a real meeting or conversation.' },
  { icon: '📺', text: 'Assista 10 minutos de conteúdo em inglês sem legenda.', en: 'Watch 10 minutes of English content without subtitles.' },
  { icon: '💡', text: 'Apresente uma ideia em inglês — mesmo que para você mesmo.', en: 'Present an idea in English — even if just to yourself.' },
  { icon: '📰', text: 'Leia um artigo em inglês sobre um tema da sua área.', en: 'Read an article in English about a topic in your field.' },
  { icon: '💬', text: 'Descreva seu fim de semana para alguém em inglês.', en: 'Describe your weekend to someone in English.' },
  { icon: '🔗', text: 'Use um phrasal verb em uma conversa real esta semana.', en: 'Use a phrasal verb in a real conversation this week.' },
  { icon: '🔍', text: 'Faça uma pesquisa no Google em inglês sobre algo do seu trabalho.', en: 'Search Google in English for a work-related topic.' },
  { icon: '🧠', text: 'Pense em voz alta em inglês por 2 minutos sobre o seu dia.', en: 'Think out loud in English for 2 minutes about your day.' },
  { icon: '🤝', text: 'Dê um feedback para alguém em inglês — mesmo que curto.', en: 'Give feedback to someone in English — even if brief.' },
];

// ── FLASH CARD GAME ───────────────────────────────────────────────────────────
function FlashCardGame({ questions, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);

  const mark = (knew) => {
    const next = [...results, knew ? 1 : 0];
    setResults(next);
    if (idx + 1 >= questions.length) {
      setDone(true);
      onComplete(next.filter(Boolean).length, next.length);
    } else {
      setFlipped(false);
      setTimeout(() => setIdx(i => i + 1), 150);
    }
  };

  if (done) return null;
  const card = questions[idx];

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ marginBottom: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#7c3aed' }}>
        {idx + 1} / {questions.length}
      </div>
      <div style={{ height: '6px', background: '#ede9fe', borderRadius: '99px', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx) / questions.length) * 100}%`, background: '#7c3aed', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#f5f3ff', border: '1.5px solid #ddd6fe', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', fontSize: '0.82rem', fontWeight: '700', color: '#6d28d9' }}>
        💭 Tente lembrar o significado em inglês antes de virar o card.
      </div>

      {/* Card */}
      <div onClick={() => setFlipped(f => !f)} style={{ background: flipped ? '#7c3aed' : 'white', border: '2px solid #ede9fe', borderRadius: '24px', padding: '48px 28px', marginBottom: '24px', cursor: 'pointer', transition: 'all 0.25s', boxShadow: '0 8px 32px rgba(124,58,237,0.12)', minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', userSelect: 'none' }}>
        {!flipped ? (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '2px' }}>🇧🇷 Português</div>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#1e1b4b' }}>{card.pt}</div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600', marginTop: '8px' }}>Toque para ver em inglês</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px' }}>🇺🇸 English</div>
            <div style={{ fontSize: '1.2rem', fontWeight: '900', color: 'white' }}>to {card.front}</div>
            <div style={{ fontSize: '2rem', fontWeight: '900', color: '#c4b5fd', marginTop: '4px' }}>{card.back}</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', marginTop: '8px' }}>past simple • past participle</div>
          </>
        )}
      </div>

      {flipped && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <button onClick={() => mark(false)} style={{ padding: '18px', background: '#fff1f2', border: '2px solid #fecdd3', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#be123c', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            😅 Errei
          </button>
          <button onClick={() => mark(true)} style={{ padding: '18px', background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#15803d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            ✅ Acertei
          </button>
        </div>
      )}
      {!flipped && (
        <div style={{ fontSize: '0.78rem', color: '#a78bfa', fontWeight: '600' }}>Toque no card para revelar</div>
      )}
    </div>
  );
}

// ── COMPLETE GAME ─────────────────────────────────────────────────────────────
function CompleteGame({ questions, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const pick = (optIdx) => {
    if (selected !== null) return;
    setSelected(optIdx);
    const correct = optIdx === questions[idx].answer;
    const ns = correct ? score + 1 : score;
    setTimeout(() => {
      if (idx + 1 >= questions.length) { setDone(true); onComplete(ns, questions.length); }
      else { setIdx(i => i + 1); setSelected(null); setScore(ns); }
    }, 1200);
  };

  if (done) return null;
  const q = questions[idx];

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#0284c7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{idx + 1} / {questions.length}</span>
        <span>✅ {score} certas</span>
      </div>
      <div style={{ height: '6px', background: '#e0f2fe', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(idx / questions.length) * 100}%`, background: '#0284c7', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>Complete a frase:</div>
        <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>
          {q.sentence.split('_____').map((part, i, arr) => (
            <span key={i}>{part}{i < arr.length - 1 && <span style={{ background: selected !== null ? (selected === q.answer ? '#bbf7d0' : '#fecdd3') : '#e0f2fe', borderBottom: '2px solid #0284c7', padding: '0 12px', borderRadius: '6px', fontWeight: '900', color: selected !== null ? (selected === q.answer ? '#15803d' : '#be123c') : '#0284c7' }}>{selected !== null ? q.options[q.answer] : '  ?  '}</span>}</span>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {q.options.map((opt, i) => {
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected !== null) {
            if (i === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (i === selected && selected !== q.answer) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
          }
          return (
            <button key={i} onClick={() => pick(i)} style={{ padding: '16px 12px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: selected !== null ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'center' }}>
              {opt}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5 }}>
          💡 {q.tip}
        </div>
      )}
    </div>
  );
}

// ── WORD BUILDER GAME ─────────────────────────────────────────────────────────
function WordBuilderGame({ questions, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [built, setBuilt] = useState([]);
  const [remaining, setRemaining] = useState([]);
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (questions[idx]) {
      const shuffled = seededShuffle([...questions[idx].words], getDayOfYear() + idx * 7);
      setRemaining(shuffled.map((w, i) => ({ w, id: i })));
      setBuilt([]); setChecked(false); setCorrect(null);
    }
  }, [idx, questions]);

  const tap = (item) => {
    if (checked) return;
    setRemaining(r => r.filter(x => x.id !== item.id));
    setBuilt(b => [...b, item]);
  };
  const remove = (item) => {
    if (checked) return;
    setBuilt(b => b.filter(x => x.id !== item.id));
    setRemaining(r => [...r, item]);
  };
  const check = () => {
    const q = questions[idx];
    const isCorrect = built.map(x => x.w).join(' ') === q.words.join(' ');
    setCorrect(isCorrect);
    setChecked(true);
    const ns = isCorrect ? score + 1 : score;
    setTimeout(() => {
      if (idx + 1 >= questions.length) { setDone(true); onComplete(ns, questions.length); }
      else { setIdx(i => i + 1); setScore(ns); }
    }, 1400);
  };

  if (done) return null;
  const q = questions[idx];

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#16a34a', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
        <span>✅ {score} certas</span>
      </div>
      <div style={{ height: '6px', background: '#dcfce7', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(idx / questions.length) * 100}%`, background: '#16a34a', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '18px', padding: '18px', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>🇧🇷 Traduza para o inglês:</div>
        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '1rem', lineHeight: 1.5 }}>{q.pt}</div>
      </div>

      {/* Built sentence area */}
      <div style={{ minHeight: '64px', background: checked ? (correct ? '#f0fdf4' : '#fff1f2') : '#f8fafc', border: `2px ${checked ? 'solid' : 'dashed'} ${checked ? (correct ? '#86efac' : '#fecdd3') : '#cbd5e1'}`, borderRadius: '16px', padding: '12px 16px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {built.length === 0 && !checked && <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600' }}>Toque nas palavras abaixo...</span>}
        {built.map(item => (
          <button key={item.id} onClick={() => remove(item)} style={{ background: checked ? 'transparent' : '#1a2a6c', color: checked ? (correct ? '#15803d' : '#be123c') : 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: '800', fontSize: '0.9rem', cursor: checked ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s' }}>
            {item.w}
          </button>
        ))}
      </div>

      {/* Word pool */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {remaining.map(item => (
          <button key={item.id} onClick={() => tap(item)} style={{ background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '10px 16px', fontWeight: '700', fontSize: '0.9rem', color: '#0f172a', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', active: { transform: 'scale(0.95)' } }}>
            {item.w}
          </button>
        ))}
      </div>

      {!checked && (
        <button onClick={check} disabled={built.length === 0} style={{ width: '100%', padding: '16px', background: built.length > 0 ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#f1f5f9', color: built.length > 0 ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: built.length > 0 ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Verificar ✓
        </button>
      )}
      {checked && (
        <div style={{ textAlign: 'center', padding: '12px', fontWeight: '800', fontSize: '1.1rem', color: correct ? '#15803d' : '#be123c' }}>
          {correct ? '🎉 Perfeito!' : `❌ Era: "${q.words.join(' ')}"`}
        </div>
      )}
    </div>
  );
}

// ── ODD ONE OUT GAME ──────────────────────────────────────────────────────────
function OddOutGame({ questions, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const pick = (word) => {
    if (selected) return;
    setSelected(word);
    const correct = word === questions[idx].answer;
    const ns = correct ? score + 1 : score;
    setTimeout(() => {
      if (idx + 1 >= questions.length) { setDone(true); onComplete(ns, questions.length); }
      else { setIdx(i => i + 1); setSelected(null); setScore(ns); }
    }, 1400);
  };

  if (done) return null;
  const q = questions[idx];

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#d97706', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
        <span>✅ {score} certas</span>
      </div>
      <div style={{ height: '6px', background: '#fef3c7', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(idx / questions.length) * 100}%`, background: '#d97706', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#d97706', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>Qual não pertence ao grupo?</div>
        <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Identifique o intruso</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        {q.words.map(word => {
          const isAnswer = word === q.answer;
          const isSelected = word === selected;
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected) {
            if (isAnswer) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            if (isSelected && !isAnswer) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            if (isSelected && isAnswer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            if (!isAnswer && !isSelected) { bg = '#f8fafc'; color = '#94a3b8'; }
          }
          return (
            <button key={word} onClick={() => pick(word)} style={{ padding: '20px 12px', background: bg, border, borderRadius: '16px', fontWeight: '800', fontSize: '1rem', color, cursor: selected ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s', textAlign: 'center' }}>
              {word}
            </button>
          );
        })}
      </div>

      {selected && (
        <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5 }}>
          💡 {q.reason}
        </div>
      )}
    </div>
  );
}

// ── PHRASAL VERB GAME ─────────────────────────────────────────────────────────
function PhrasalGame({ questions, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const pick = (opt) => {
    if (selected) return;
    setSelected(opt);
    const correct = opt === questions[idx].pt;
    const ns = correct ? score + 1 : score;
    setTimeout(() => {
      if (idx + 1 >= questions.length) { setDone(true); onComplete(ns, questions.length); }
      else { setIdx(i => i + 1); setSelected(null); setScore(ns); }
    }, 1300);
  };

  if (done) return null;
  const q = questions[idx];
  const shuffledOpts = seededShuffle(q.options, getDayOfYear() + idx * 13);

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#be185d', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
        <span>✅ {score} certas</span>
      </div>
      <div style={{ height: '6px', background: '#fce7f3', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(idx / questions.length) * 100}%`, background: '#be185d', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: 'linear-gradient(135deg,#fdf2f8,#fce7f3)', border: '2px solid #f9a8d4', borderRadius: '20px', padding: '28px', textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#be185d', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>O que significa?</div>
        <div style={{ fontSize: '2rem', fontWeight: '900', color: '#1e1b4b' }}>to {q.verb}</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {shuffledOpts.map(opt => {
          const isAnswer = opt === q.pt;
          const isSelected = opt === selected;
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected) {
            if (isAnswer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (isSelected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            else { bg = '#f8fafc'; color = '#94a3b8'; }
          }
          return (
            <button key={opt} onClick={() => pick(opt)} style={{ padding: '16px 20px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: selected ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s', textAlign: 'left' }}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── SCORE SCREEN ──────────────────────────────────────────────────────────────
function ScoreScreen({ score, total, gameType, onSave, saving, saved }) {
  const pct = Math.round((score / total) * 100);
  const info = GAME_INFO[gameType];
  const msg = pct === 100 ? '🏆 Perfeito!' : pct >= 80 ? '🔥 Muito bem!' : pct >= 60 ? '👍 Bom trabalho!' : '💪 Continue praticando!';

  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: info.bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.8rem', margin: '0 auto 20px', boxShadow: `0 12px 36px ${info.color}40` }}>
        {info.icon}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#0f172a', marginBottom: '4px' }}>{msg}</div>
      <div style={{ fontSize: '3.5rem', fontWeight: '900', color: info.color, lineHeight: 1, margin: '16px 0 6px' }}>{score}<span style={{ fontSize: '1.5rem', color: '#94a3b8' }}>/{total}</span></div>
      <div style={{ fontSize: '1rem', fontWeight: '700', color: '#64748b', marginBottom: '32px' }}>{pct}% de aproveitamento</div>
      {!saved ? (
        <button onClick={onSave} disabled={saving} style={{ width: '100%', padding: '16px', background: saving ? '#f1f5f9' : info.bg, color: saving ? '#94a3b8' : 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '1rem', cursor: saving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: saving ? 'none' : `0 4px 16px ${info.color}40` }}>
          {saving ? 'Salvando...' : '💾 Salvar resultado'}
        </button>
      ) : (
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.3rem' }}>✅</span>
          <span style={{ fontWeight: '800', color: '#15803d' }}>Resultado salvo! Volte amanhã.</span>
        </div>
      )}
    </div>
  );
}

// ── MAIN PRACTICE COMPONENT ───────────────────────────────────────────────────
export default function Practice({ session, onBack }) {
  const [tab, setTab] = useState('game');
  const [gameState, setGameState] = useState('menu'); // 'menu' | 'intro' | 'playing' | 'score'
  const [selectedGame, setSelectedGame] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [playedGamesToday, setPlayedGamesToday] = useState(new Set());
  const [gameStreak, setGameStreak] = useState(0);
  const [totalGames, setTotalGames] = useState(0);
  const [missionDone, setMissionDone] = useState(false);
  const [missionSaving, setMissionSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const todayGame = getTodayGameType();
  const todayPhrase = getTodayPhrase();
  const currentMission = getCurrentMission();
  const weekNum = getWeekNumber();
  const yearNum = new Date().getFullYear();

  const loadData = useCallback(async () => {
    setLoadingData(true);
    const [{ data: comps }, { data: missions }] = await Promise.all([
      supabase.from('game_completions').select('played_date, game_type').eq('student_id', session.id).order('played_date', { ascending: false }),
      supabase.from('mission_completions').select('id').eq('student_id', session.id).eq('week_number', weekNum).eq('year', yearNum),
    ]);
    if (comps) {
      setTotalGames(comps.length);
      const todayComps = comps.filter(c => c.played_date === todayISO());
      setPlayedGamesToday(new Set(todayComps.map(c => c.game_type)));
      // Calculate streak
      const dates = [...new Set(comps.map(c => c.played_date))].sort().reverse();
      const today = todayISO();
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (dates.length && (dates[0] === today || dates[0] === yesterday)) {
        let s = 1;
        for (let i = 1; i < dates.length; i++) {
          const diff = (new Date(dates[i-1]) - new Date(dates[i])) / 86400000;
          if (diff === 1) s++; else break;
        }
        setGameStreak(s);
      } else { setGameStreak(0); }
    }
    setMissionDone(!!(missions && missions.length > 0));
    setLoadingData(false);
  }, [session.id, weekNum, yearNum]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGameComplete = (s, t) => {
    setScore(s); setTotal(t); setGameState('score');
  };

  const saveResult = async () => {
    setSaving(true);
    await supabase.from('game_completions').insert({
      student_id: session.id, game_type: selectedGame,
      score, total, played_date: todayISO(),
    });
    setSaving(false); setSaved(true);
    setPlayedGamesToday(prev => new Set([...prev, selectedGame]));
    loadData();
  };

  const saveMission = async () => {
    setMissionSaving(true);
    await supabase.from('mission_completions').insert({
      student_id: session.id, mission_text: currentMission.text,
      week_number: weekNum, year: yearNum,
    });
    setMissionSaving(false); setMissionDone(true);
  };

  const questions = getTodayQuestions(selectedGame || todayGame);
  const info = GAME_INFO[selectedGame || todayGame];
  const gameAlreadyDone = playedGamesToday.has(selectedGame);

  if (loadingData) return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontWeight: '600' }}>Carregando...</div>
  );

  return (
    <>
      <style>{practiceStyles}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => onBack()} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>
          ← Portal
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: '900', fontSize: '1.2rem', color: '#0f172a', lineHeight: 1 }}>🎮 Praticar</div>
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', marginTop: '3px' }}>Novo jogo, frase e missão todo dia</div>
        </div>
        {gameStreak > 0 && (
          <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '50px', padding: '5px 12px', fontSize: '0.75rem', fontWeight: '800', color: '#ea580c', whiteSpace: 'nowrap' }}>
            🔥 {gameStreak}d
          </div>
        )}
      </div>

      {/* Tab nav */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', background: '#f1f5f9', borderRadius: '16px', padding: '5px', marginBottom: '24px' }}>
        {[['game', '🎮', 'Jogo'], ['phrase', '💬', 'Frase'], ['mission', '🌍', 'Missão']].map(([key, icon, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '12px 6px', background: tab === key ? 'white' : 'transparent', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '0.82rem', color: tab === key ? '#0f172a' : '#64748b', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: tab === key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
            {icon} {label}
          </button>
        ))}
      </div>

      {/* ── GAME TAB ── */}
      {tab === 'game' && (
        <div className="practice-card">

          {/* MENU */}
          {gameState === 'menu' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>⭐ Jogo do dia</div>
                <button onClick={() => { setSelectedGame(todayGame); setSaved(false); setGameState('intro'); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', background: GAME_INFO[todayGame].bg, color: 'white', padding: '18px 20px', borderRadius: '18px', border: 'none', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${GAME_INFO[todayGame].color}40`, textAlign: 'left', position: 'relative', overflow: 'hidden' }}>
                  <span style={{ fontSize: '2.2rem', flexShrink: 0 }}>{GAME_INFO[todayGame].icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '900', fontSize: '1rem', marginBottom: '2px' }}>{GAME_INFO[todayGame].name}</div>
                    <div style={{ fontWeight: '600', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)' }}>{GAME_INFO[todayGame].desc}</div>
                  </div>
                  {playedGamesToday.has(todayGame)
                    ? <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '50px', padding: '4px 12px', fontSize: '0.72rem', fontWeight: '800', flexShrink: 0 }}>✓ Feito</span>
                    : <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '50px', padding: '4px 12px', fontSize: '0.72rem', fontWeight: '800', flexShrink: 0 }}>Jogar →</span>
                  }
                </button>
              </div>

              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Todos os jogos</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {GAME_TYPES.map(type => {
                  const g = GAME_INFO[type];
                  const isToday = type === todayGame;
                  const isDone = playedGamesToday.has(type);
                  return (
                    <button key={type} onClick={() => { setSelectedGame(type); setSaved(false); setGameState('intro'); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: isDone ? '#f8fafc' : 'white', border: `1.5px solid ${isToday ? g.color + '40' : '#f1f5f9'}`, borderRadius: '14px', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', textAlign: 'left', transition: 'all 0.15s' }}>
                      <span style={{ fontSize: '1.6rem', flexShrink: 0, width: '36px', height: '36px', background: g.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{g.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '800', color: isDone ? '#94a3b8' : '#0f172a', fontSize: '0.9rem' }}>{g.name}</div>
                        <div style={{ fontWeight: '600', color: '#94a3b8', fontSize: '0.75rem', marginTop: '1px' }}>{g.desc}</div>
                      </div>
                      {isToday && !isDone && <span style={{ background: g.color + '15', color: g.color, borderRadius: '50px', padding: '3px 10px', fontSize: '0.65rem', fontWeight: '800', flexShrink: 0 }}>Hoje</span>}
                      {isDone && <span style={{ color: '#16a34a', fontWeight: '800', fontSize: '0.9rem', flexShrink: 0 }}>✓</span>}
                    </button>
                  );
                })}
              </div>

              {gameStreak > 0 && (
                <div style={{ marginTop: '16px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.3rem' }}>🔥</span>
                  <div style={{ fontWeight: '700', color: '#ea580c', fontSize: '0.85rem' }}>{gameStreak} dia{gameStreak !== 1 ? 's' : ''} de sequência · {totalGames} jogos no total</div>
                </div>
              )}
            </div>
          )}

          {/* INTRO */}
          {gameState === 'intro' && info && (
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => setGameState('menu')} style={{ display: 'block', marginBottom: '20px', background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>← Escolher outro jogo</button>
              <div style={{ width: '90px', height: '90px', borderRadius: '24px', background: info.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', margin: '0 auto 20px', boxShadow: `0 12px 36px ${info.color}35` }}>
                {info.icon}
              </div>
              <div style={{ fontSize: '0.7rem', fontWeight: '800', color: info.color, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>{selectedGame === todayGame ? 'Jogo de hoje' : 'Prática livre'}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a', marginBottom: '6px' }}>{info.name}</div>
              <div style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: '600', marginBottom: '8px' }}>{info.desc}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600', marginBottom: '24px' }}>O tipo de jogo do dia muda todo dia — volte amanhã para um diferente.</div>
              <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '16px', marginBottom: '28px', display: 'flex', justifyContent: 'center', gap: '32px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a' }}>{questions.length}</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Questões</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a' }}>~3 min</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duração</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '900', color: totalGames > 0 ? '#16a34a' : '#0f172a' }}>{totalGames}</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total jogados</div>
                </div>
              </div>
              {gameAlreadyDone ? (
                <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '1.3rem' }}>✅</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '800', color: '#15803d', fontSize: '0.9rem' }}>Você já jogou este hoje!</div>
                    <div style={{ fontWeight: '600', color: '#16a34a', fontSize: '0.78rem' }}>Pode jogar de novo ou escolher outro.</div>
                  </div>
                </div>
              ) : null}
              <button onClick={() => setGameState('playing')} style={{ width: '100%', padding: '18px', background: info.bg, color: 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${info.color}40`, letterSpacing: '0.5px' }}>
                {gameAlreadyDone ? 'Jogar novamente →' : 'Começar agora →'}
              </button>
            </div>
          )}

          {gameState === 'playing' && info && (
            <>
              {selectedGame === 'flashcard'   && <FlashCardGame  questions={questions} onComplete={handleGameComplete} />}
              {selectedGame === 'complete'    && <CompleteGame   questions={questions} onComplete={handleGameComplete} />}
              {selectedGame === 'wordbuilder' && <WordBuilderGame questions={questions} onComplete={handleGameComplete} />}
              {selectedGame === 'oddout'      && <OddOutGame     questions={questions} onComplete={handleGameComplete} />}
              {selectedGame === 'phrasal'     && <PhrasalGame    questions={questions} onComplete={handleGameComplete} />}
            </>
          )}

          {gameState === 'score' && selectedGame && (
            <>
              <ScoreScreen score={score} total={total} gameType={selectedGame} onSave={saveResult} saving={saving} saved={saved} />
              {saved && (
                <button onClick={() => { setGameState('menu'); setSaved(false); }} style={{ width: '100%', marginTop: '12px', padding: '14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  ← Voltar ao menu de jogos
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PHRASE TAB ── */}
      {tab === 'phrase' && (
        <div className="practice-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#1a2a6c', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '20px' }}>
            💬 Frase do dia
          </div>
          <div style={{ background: 'linear-gradient(135deg,#1a2a6c,#2563eb)', borderRadius: '20px', padding: '32px 24px', marginBottom: '20px', boxShadow: '0 12px 36px rgba(26,42,108,0.25)' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: 'white', lineHeight: 1.3, marginBottom: '16px' }}>
              "{todayPhrase.en}"
            </div>
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', marginBottom: '16px' }} />
            <div style={{ fontSize: '1rem', fontWeight: '700', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
              {todayPhrase.pt}
            </div>
          </div>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', textAlign: 'left' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Exemplo de uso:</div>
            <div style={{ fontSize: '0.92rem', fontWeight: '600', color: '#475569', fontStyle: 'italic', lineHeight: 1.6 }}>{todayPhrase.ex}</div>
          </div>
          <div style={{ marginTop: '20px', fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600' }}>
            Nova frase todo dia — 60 expressões no total.
          </div>
        </div>
      )}

      {/* ── MISSION TAB ── */}
      {tab === 'mission' && (
        <div className="practice-card" style={{ textAlign: 'center' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.4rem', margin: '0 auto 20px', boxShadow: '0 12px 36px rgba(37,99,235,0.3)' }}>
            {currentMission.icon}
          </div>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>Missão da semana</div>
          <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', lineHeight: 1.5, marginBottom: '10px' }}>
            {currentMission.text}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: '600', fontStyle: 'italic', marginBottom: '28px' }}>
            {currentMission.en}
          </div>
          <div style={{ background: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: '14px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#4338ca', lineHeight: 1.6 }}>
              🎯 Faça isso em algum momento desta semana — em uma reunião, e-mail, conversa ou até praticando sozinho. O objetivo é sair da zona de conforto no inglês.
            </div>
          </div>
          {missionDone ? (
            <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', padding: '20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '2rem' }}>🏅</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '900', color: '#15803d', fontSize: '1rem' }}>Missão cumprida!</div>
                <div style={{ fontWeight: '600', color: '#16a34a', fontSize: '0.8rem' }}>Nova missão na próxima semana.</div>
              </div>
            </div>
          ) : (
            <button onClick={saveMission} disabled={missionSaving} style={{ width: '100%', padding: '18px', background: missionSaving ? '#f1f5f9' : 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: missionSaving ? '#94a3b8' : 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: missionSaving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: missionSaving ? 'none' : '0 6px 20px rgba(37,99,235,0.3)' }}>
              {missionSaving ? 'Salvando...' : '✅ Missão cumprida!'}
            </button>
          )}
        </div>
      )}
    </>
  );
}

const practiceStyles = `
  .practice-card { background: white; border-radius: 24px; padding: 28px 22px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); margin-bottom: 20px; }
  @media (max-width: 480px) { .practice-card { padding: 22px 16px; } }
`;
